import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { EnrichedFeature } from '@/hooks/useGeoLayer';
import { HEBREW_OSM_BASEMAP } from '@/components/map/map-basemap.util';
import {
  EASTERN_GALILEE_CENTER,
  EASTERN_GALILEE_DEFAULT_ZOOM,
  EASTERN_GALILEE_LEAFLET_BOUNDS,
  EASTERN_GALILEE_MAX_ZOOM,
  EASTERN_GALILEE_MIN_ZOOM,
} from '@/lib/map/eastern-galilee';

// Fix default leaflet icon paths broken by Vite bundling
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATUS_ICON_COLORS: Record<string, string> = {
  active: '#00c875',
  lead: '#fdab3d',
  inactive: '#c4c4c4',
};

function makeIcon(status: string) {
  const color = STATUS_ICON_COLORS[status] ?? '#579bfc';
  return L.divIcon({
    className: '',
    html: `<div style="
      width:14px;height:14px;
      border-radius:50%;
      background:${color};
      border:2px solid #fff;
      box-shadow:0 1px 4px rgba(0,0,0,.4);
    "></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });
}

function FlyTo({ feature }: { feature: EnrichedFeature | null }) {
  const map = useMap();
  const prev = useRef<string | number | null>(null);

  useEffect(() => {
    if (!feature?.geometry || feature.id === prev.current) return;
    prev.current = feature.id;
    map.flyTo([feature.geometry.lat, feature.geometry.lng], 14, { duration: 0.8 });
  }, [feature, map]);

  return null;
}

interface Props {
  features: EnrichedFeature[];
  selected: EnrichedFeature | null;
  onSelect: (feature: EnrichedFeature) => void;
}

const POLYGON_STYLE = { color: '#579bfc', weight: 2, fillOpacity: 0.22 };
const POLYGON_SELECTED_STYLE = { color: '#0073ea', weight: 3, fillOpacity: 0.35 };

export function SiteMap({ features, selected, onSelect }: Props) {
  const withPoint = features.filter((f) => f.geometry && !f.geometry.shape);
  const withPolygon = features.filter((f) => f.geometry?.shape);

  return (
    <MapContainer
      center={EASTERN_GALILEE_CENTER}
      zoom={EASTERN_GALILEE_DEFAULT_ZOOM}
      minZoom={EASTERN_GALILEE_MIN_ZOOM}
      maxZoom={EASTERN_GALILEE_MAX_ZOOM}
      maxBounds={EASTERN_GALILEE_LEAFLET_BOUNDS}
      maxBoundsViscosity={1}
      style={{ height: '100%', width: '100%' }}
    >
      <TileLayer
        attribution={HEBREW_OSM_BASEMAP.attribution}
        url={HEBREW_OSM_BASEMAP.url}
        subdomains={HEBREW_OSM_BASEMAP.subdomains}
      />

      <FlyTo feature={selected} />

      {withPolygon.map((f) => (
        <GeoJSON
          key={`poly-${f.id}`}
          data={
            {
              type: 'Feature',
              geometry: f.geometry!.shape!,
              properties: { id: f.id, name: f.name },
            } as GeoJSON.Feature<GeoJSON.Polygon>
          }
          style={() =>
            selected?.id === f.id ? POLYGON_SELECTED_STYLE : POLYGON_STYLE
          }
          eventHandlers={{ click: () => onSelect(f) }}
        />
      ))}

      {withPoint.map((f) => (
        <Marker
          key={f.id}
          position={[f.geometry!.lat, f.geometry!.lng]}
          icon={makeIcon(f.crm.status)}
          eventHandlers={{ click: () => onSelect(f) }}
        >
          <Popup>
            <strong>{f.name}</strong>
            <br />
            {f.crm.manager} · {f.crm.phone}
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
